import { useState } from 'react';
import { categories } from '@/mocks/categories';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setFormStatus('success');
      setEmail('');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d992srag3iubr452ii7g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          email: formData.get('email') as string || '',
        }).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormError('');
        setEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setFormStatus('error');
      setFormError('Network error. Please check your connection and try again.');
    }
  };

  return (
    <footer className="bg-foreground-950 rounded-t-3xl mt-8">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 pt-14 pb-8 md:pt-20 md:pb-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-14">
          <div className="lg:col-span-1">
            <Link to="/" className="font-heading text-xl md:text-2xl font-semibold text-white mb-4 inline-block">
              Hipobuy Sheet Hub
            </Link>
            <p className="text-foreground-300 text-sm leading-relaxed mt-3 max-w-xs">
              The ultimate <strong className="text-foreground-200">hipobuy spreadsheet</strong> database and shopping research platform. Discover, compare, and organize products smarter.
            </p>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4">Quick Links</h4>
            <ul className="space-y-2.5">
              <li>
                <Link to="/" className="text-foreground-300 text-sm hover:text-white transition-colors whitespace-nowrap">Home</Link>
              </li>
              <li>
                <a href="#categories" className="text-foreground-300 text-sm hover:text-white transition-colors whitespace-nowrap">Categories</a>
              </li>
              <li>
                <Link to="/hipobuy-spreadsheet-guide" className="text-foreground-300 text-sm hover:text-white transition-colors whitespace-nowrap">Shopping Guide</Link>
              </li>
              <li>
                <Link to="/hipobuy-spreadsheet-vs-kakobuy" className="text-foreground-300 text-sm hover:text-white transition-colors whitespace-nowrap">Platform Comparison</Link>
              </li>
              <li>
                <Link to="/hipobuy-spreadsheet-safety" className="text-foreground-300 text-sm hover:text-white transition-colors whitespace-nowrap">Safety Guide</Link>
              </li>
              <li>
                <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-300 text-sm hover:text-white transition-colors whitespace-nowrap">
                  hipobuy sheet
                  <i className="ri-external-link-line text-xs ml-1 w-3 h-3 inline-flex items-center justify-center"></i>
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4">Product Categories</h4>
            <ul className="space-y-2.5">
              {categories.slice(0, 7).map((cat) => (
                <li key={cat.name}>
                  <a
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-foreground-300 text-sm hover:text-white transition-colors whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                </li>
              ))}
              <li>
                <a href="#categories" className="text-accent-400 text-sm hover:text-accent-300 transition-colors whitespace-nowrap">
                  View all categories →
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-semibold text-sm mb-4">Stay Updated</h4>
            <p className="text-foreground-300 text-xs mb-4">
              Get the latest <strong className="text-foreground-200">hipobuy spreadsheet</strong> updates and new category additions.
            </p>
            <form
              onSubmit={handleNewsletterSubmit}
              data-readdy-form=""
              className="flex gap-0"
            >
              <div className="honeypot-field">
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                required
                className="flex-1 bg-white/10 border border-white/15 rounded-l-full px-4 py-2.5 text-sm text-white placeholder:text-foreground-400 focus:outline-none focus:border-white/30 transition-colors"
              />
              <button
                type="submit"
                className="bg-accent-500 hover:bg-accent-600 text-white px-4 py-2.5 rounded-r-full transition-colors cursor-pointer flex items-center justify-center"
              >
                <i className="ri-arrow-right-line text-base w-4 h-4 flex items-center justify-center"></i>
              </button>
            </form>
            {formStatus === 'success' && (
              <p className="text-accent-400 text-xs mt-2">You&apos;re subscribed! Check your inbox.</p>
            )}
            {formStatus === 'error' && (
              <p className="text-red-400 text-xs mt-2">{formError}</p>
            )}
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-foreground-400 text-xs">
            &copy; 2026 Hipobuy Spreadsheet Hub. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-400 text-xs hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-foreground-400 text-xs hover:text-white transition-colors">
              Terms of Service
            </a>
          </div>
        </div>
      </div>

      <div className="w-full py-8 overflow-hidden select-none pointer-events-none">
        <p className="text-[80px] md:text-[120px] font-heading font-bold text-white/[0.03] whitespace-nowrap leading-none">
          HIPOBUY SPREADSHEET HUB
        </p>
      </div>
    </footer>
  );
}